import { useEffect, useState } from 'react'
import { Spin } from 'antd'
import ReactECharts from 'echarts-for-react'
import { useParams } from 'react-router-dom'
import { getStockKlines } from '@/api/stock'
import type { StockKlinePoint } from '@/types/stockPool'
import type { KLine } from '../../types'
import { buildCandlestickOption } from '@/utils/kline'

/** 将 fallback 的 KLine 映射为 StockKlinePoint（保留 OHLC） */
function toPoints(klines: KLine[]): StockKlinePoint[] {
    return klines.map(k => ({
        date: k.ts,
        open: k.open,
        high: k.high,
        low: k.low,
        close: k.close,
        volume: k.volume,
    }))
}

export default function KChart({ fallbackKlines, name }: { fallbackKlines?: KLine[]; name: string }) {
    const { code } = useParams<{ code: string }>()
    const [points, setPoints] = useState<StockKlinePoint[]>([])
    const [loading, setLoading] = useState(true)
    const [usingMock, setUsingMock] = useState(false)

    useEffect(() => {
        if (!code) return
        setLoading(true)
        getStockKlines(decodeURIComponent(code), 60)
            .then(data => {
                if (data.length > 0) {
                    setPoints(data)
                    setUsingMock(false)
                } else if (fallbackKlines?.length) {
                    setPoints(toPoints(fallbackKlines))
                    setUsingMock(true)
                }
            })
            .catch(() => {
                if (fallbackKlines?.length) {
                    setPoints(toPoints(fallbackKlines))
                    setUsingMock(true)
                }
            })
            .finally(() => setLoading(false))
    }, [code])

    if (loading) return <div style={{ padding: '60px 0', textAlign: 'center' }}><Spin /></div>

    if (!points.length) {
        return <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-3)' }}>暂无K线数据</div>
    }

    const last = points[points.length - 1]
    const dayChg = last.change_pct
    // 无单日涨跌幅时退回区间累计，仅作兜底
    const firstClose = points[0].close
    const fallbackChg = firstClose ? ((last.close - firstClose) / firstClose * 100) : null
    const displayChg = dayChg ?? fallbackChg
    const isUp = (displayChg ?? 0) >= 0

    return (
        <div className="skd__kchart">
            <div className="skd__kchart-header">
                <span className="skd__kchart-title">{name} · 日K线（近60交易日）</span>
                <span className={`skd__kchart-chg ${isUp ? 'pos' : 'neg'}`}>
                    {displayChg == null
                        ? '--'
                        : `${isUp ? '▲' : '▼'} ${displayChg >= 0 ? '+' : ''}${displayChg.toFixed(2)}%`}
                </span>
                {usingMock && <span className="skd__kchart-mock">演示数据</span>}
            </div>
            <ReactECharts option={buildCandlestickOption(points)} style={{ height: 380 }} notMerge lazyUpdate />
        </div>
    )
}
